// Lógica pura de la vista de celular de la carta Gantt: agrupa las tareas por urgencia
// (en vez de dibujar la rejilla) y calcula la mini-barra de cada tarjeta. Las fechas son
// strings ISO 'YYYY-MM-DD'; la aritmética de días usa dayjs y las comparaciones el orden
// lexicográfico del formato ISO (mismo criterio que ganttGeometry).
import dayjs from 'libs/dayjs';
import { columnaDeFecha } from './ganttGeometry';

const tieneFechas = (t) => Boolean(t?.planned_start && t?.planned_end);

// Orden en que se muestran los grupos.
const GRUPOS = [
  { key: 'atrasadas', label: 'Atrasadas' },
  { key: 'semana', label: 'Esta semana' },
  { key: 'proximas', label: 'Próximas' },
  { key: 'sin-fechas', label: 'Sin fechas' },
  { key: 'terminadas', label: 'Terminadas' },
];

// Ancho mínimo (en %) de la mini-barra, para que una tarea de un día se vea.
const ANCHO_MINIMO = 2;

/**
 * Tareas sin subtareas. Las que tienen hijas no se listan: su barra y sus fechas se
 * derivan de las hijas, así que listarlas duplicaría la información.
 * @param {Array} tareas
 * @returns {Array}
 */
export function tareasHoja(tareas) {
  const conHijas = new Set(
    (tareas || []).filter((t) => t.parent_id != null).map((t) => t.parent_id)
  );
  return (tareas || []).filter((t) => !conHijas.has(t.id));
}

/**
 * Grupo de urgencia de una tarea:
 *   terminadas → `finish` verdadero (gana sobre todo lo demás);
 *   sin-fechas → le falta inicio o fin;
 *   atrasadas  → su fin es anterior a hoy;
 *   semana     → vigente o por empezar hasta el domingo de la semana ISO de hoy;
 *   proximas   → empieza después de esa semana.
 * @param {object} tarea
 * @param {string} hoy 'YYYY-MM-DD'
 * @returns {'terminadas'|'sin-fechas'|'atrasadas'|'semana'|'proximas'}
 */
export function grupoDeTarea(tarea, hoy) {
  if (tarea.finish) return 'terminadas';
  if (!tieneFechas(tarea)) return 'sin-fechas';
  if (tarea.planned_end < hoy) return 'atrasadas';
  const domingo = dayjs(hoy).isoWeekday(7).format('YYYY-MM-DD');
  return tarea.planned_start <= domingo ? 'semana' : 'proximas';
}

const porFecha = (a, b) => {
  const fa = a.tarea.planned_end ?? '';
  const fb = b.tarea.planned_end ?? '';
  if (fa !== fb) return fa < fb ? -1 : 1;
  const ia = a.tarea.planned_start ?? '';
  const ib = b.tarea.planned_start ?? '';
  if (ia !== ib) return ia < ib ? -1 : 1;
  return 0;
};

/**
 * Agrupa las tareas hoja por urgencia. Solo devuelve los grupos con tareas, en el orden
 * Atrasadas → Esta semana → Próximas → Sin fechas → Terminadas. Cada ítem lleva su
 * `padre` (o null) para rotular las subtareas.
 * @param {Array} tareas
 * @param {string} hoy
 * @returns {Array<{key:string, label:string, items:Array<{tarea:object, padre:object|null}>}>}
 */
export function agruparTareasMovil(tareas, hoy) {
  const porId = new Map((tareas || []).map((t) => [t.id, t]));
  const items = tareasHoja(tareas).map((tarea) => ({
    tarea,
    padre: tarea.parent_id != null ? porId.get(tarea.parent_id) ?? null : null,
  }));

  return GRUPOS.map(({ key, label }) => ({
    key,
    label,
    items: items.filter((i) => grupoDeTarea(i.tarea, hoy) === key).sort(porFecha),
  })).filter((g) => g.items.length > 0);
}

/**
 * Posición y ancho (en % del rango de la ODT) de la mini-barra de una tarjeta.
 * @param {{planned_start:string|null, planned_end:string|null}} tarea
 * @param {{desde:string, dias:number}} rango
 * @returns {{left:number, width:number}|null} `null` si la tarea no tiene fechas.
 */
export function miniBarra(tarea, rango) {
  if (!tieneFechas(tarea) || !rango?.dias) return null;
  const col = columnaDeFecha(tarea.planned_start, rango.desde);
  const span = dayjs(tarea.planned_end).diff(dayjs(tarea.planned_start), 'day') + 1;
  return {
    left: (col / rango.dias) * 100,
    width: Math.max((span / rango.dias) * 100, ANCHO_MINIMO),
  };
}

/**
 * Tareas que se dibujan en la línea de tiempo de solo lectura: las que tienen fechas
 * propias y los padres con alguna hija fechada (su barra se deriva).
 * @param {Array} tareas
 * @returns {Array}
 */
export function tareasConBarra(tareas) {
  const lista = tareas || [];
  return lista.filter((t) => {
    const hijas = lista.filter((h) => h.parent_id === t.id);
    return hijas.length > 0 ? hijas.some(tieneFechas) : tieneFechas(t);
  });
}
